import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import { AUTH_TESTS } from './auth-test.const';
import { SIGN_UP_SRC } from '../../consts/profile.const';
import { PREVIEW_TYPES } from '../../consts/images.const';
import { RESERVATION_TYPE } from '../../consts/reservation.consts';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import SniffCarouselSSR from '../../components/carousel/carousel-ssr';
import RenderSubInfo from './render-sub-info';
import ListingHead from '../../components/listing-head';
import SniffButton from '../../components/sniff-button';
import PictureSet from '../../components/picture-set';
import { ImageHelper } from '../../classes/image-helper.class';
import { IMG_RESIZES } from '../../consts/resizer.consts';

const RenderRating = loadable(() => import('./render-rating'));
const RenderInactive = loadable(() => import('./render-inactive'));
const RenderHostInfo = loadable(() => import('./render-host-info'));
const SpotBreadcrumbs = loadable(() => import('../../components/spot-breadcrumbs'));
const RequestMorePhotos = loadable(() => import('../../components/request-more-photos'));
const SniffCarouselClient = loadable(() => import('../../components/carousel'));
const RenderMobileInactive = loadable(() => import('./render-mobile-inactive'));

const CookieService = require('../../services/cookie.service');

const RenderCarouselServer = ({ spot, myPhotos, useStateSSR, publicPark, toggleMobileGallery }) => (
    <SniffCarouselSSR
        {...{
            item: spot,
            myPhotos,
            useEffect,
            useState,
            lazy: 'eager',
            showArrow: false,
            server: true,
        }}
        onClick={() => toggleMobileGallery(true)}
        spotGallery={!publicPark}
        publicPark={publicPark}
        useStateSSR={useStateSSR}
    />
);

const RenderCarouselClient = ({ spot, myPhotos, useStateSSR, publicPark, handleSwipeMobile, mobilePhotoCount, toggleMobileGallery }) => (
    <SniffCarouselClient
        images={myPhotos}
        type={PREVIEW_TYPES.VIEW}
        disableArrow
        handleSwipeMobile={handleSwipeMobile}
        mobilePhotoCount={mobilePhotoCount}
        onClick={() => toggleMobileGallery(true)}
        spotHost={spot.host}
        spotId={spot.id}
        title={spot.title}
        state={spot.state}
        city={spot.city}
        spot={spot}
        lazy="eager"
        spotGallery={!publicPark}
        publicPark={publicPark}
        useStateSSR={useStateSSR}
    />
);

const RenderHeadMobile = ({
    spot,
    showDisTop,
    setMobilePhotoGallery,
    handleSwipeMobile,
    mobilePhotoCount,
    listBread,
    goToReviews,
    goToHost,
    handleBook,
    handleSniffpassBadge,
    userDetails,
    publicPark,
    handleChangeStars,
    rating,
    setRating,
    spotPhotos,
    useStateSSR,
    setIsBlurTest = () => {},
    isRecentReviewsTest,
}) => {
    const [Carousel, setCarousel] = useState(() => RenderCarouselServer);
    const myPhotos = publicPark ? spot?.allPhotos.map((p) => ({ imageAws: p })) : spotPhotos || spot.spotPhotos;
    const isSplitPhotos = !publicPark && myPhotos && myPhotos.length > 3;
    const excess = isSplitPhotos ? myPhotos.length - 3 : 0;
    const altImg = spot.title ? `${spot.title} - ${spot.city}, ${spot.state}` : 'Spot thumbnail';

    const toggleMobileGallery = async (bool = false) => {
        if (!userDetails?.id) {
            const re = `${window.location.pathname}#${AUTH_TESTS.MOBILE_PHOTO_GALLERY}`;
            CookieService.default.set(COOKIE_PARAM_NAME.SIGN_UP_FROM, SIGN_UP_SRC.SPOT_DETAILS_WEB, { expires: 1 });
            CookieService.default.set(COOKIE_PARAM_NAME.REDIRECT, re);
            setIsBlurTest(true);
            return;
        }

        setMobilePhotoGallery(bool);
    };

    const CarouselComp = <Carousel {...{ spot, myPhotos, useStateSSR, publicPark, handleSwipeMobile, mobilePhotoCount, toggleMobileGallery }} />;

    useEffect(() => {
        setCarousel(() => RenderCarouselClient);
    }, []);

    return (
        <div className="head">
            <div className="d-block w-100">
                {!publicPark && (!spot.live || spot.host?.id == 0) && (
                    <>
                        {showDisTop && <RenderMobileInactive spot={spot} />}
                        <RenderInactive {...{ spot, useState }} />
                    </>
                )}
                {isSplitPhotos ? (
                    <div className="mobile-split">
                        <div onClick={() => toggleMobileGallery(true)}>
                            <PictureSet
                                imgs={ImageHelper.getUrlFromOptions(myPhotos[0].imageAws, `fit-in/${IMG_RESIZES.x540}`)}
                                alt={`${altImg} #1`}
                                height={265}
                            />
                        </div>
                        <div>
                            <div onClick={() => toggleMobileGallery(true)}>
                                <PictureSet
                                    imgs={ImageHelper.getUrlFromOptions(myPhotos[1].imageAws, `fit-in/${IMG_RESIZES.x256}`)}
                                    alt={`${altImg} #2`}
                                    height={131.5}
                                />
                            </div>
                            <div onClick={() => toggleMobileGallery(true)} className="mobile-split_excess">
                                <PictureSet
                                    imgs={ImageHelper.getUrlFromOptions(myPhotos[2].imageAws, `fit-in/${IMG_RESIZES.x256}`)}
                                    alt={`${altImg} #3`}
                                    height={131.5}
                                />
                                <div className="mobile-split_excess_overlay">
                                    <span>+{excess}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mobile-carousel">
                        {myPhotos && myPhotos.length > 0 ? (
                            <div className={`spot-main-photo ${useState ? 'carou-ssr' : ''}`}>{CarouselComp}</div>
                        ) : (
                            <div className="spot-main-photo placeholder-photo">
                                <RequestMorePhotos id={spot.id} publicPark={publicPark} />
                            </div>
                        )}
                    </div>
                )}
                <SpotBreadcrumbs SSR {...{ listBread }} />
                <div className="info w-100">
                    <ListingHead
                        size={spot.size}
                        fncd={spot.enclosureType}
                        title={spot.title || spot.parkName}
                        city={spot.city}
                        state={spot.state}
                        sizeUnits={spot.sizeUnits}
                        distanceFrom={spot.distanceFrom}
                        userDetails={userDetails}
                        address={spot.address}
                        publicPark={publicPark}
                    />
                </div>
                <RenderSubInfo
                    spot={spot}
                    publicPark={publicPark}
                    handleGoTo={goToReviews}
                    handleSniffpassBadge={handleSniffpassBadge}
                    isRecentReviewsTest={isRecentReviewsTest}
                />
                {publicPark && !spot.isReviewed && (
                    <>
                        <RenderRating {...{ handleChangeStars, rating, setRating }} />
                        <div className="my-4">
                            <hr />
                        </div>
                    </>
                )}
                {!publicPark && (
                    <>
                        <RenderHostInfo spot={spot} handleGoTo={goToHost} useState={useState} />
                        <div className="container mt-2">
                            <SniffButton
                                className="mb-2"
                                color="secondary"
                                size="lg"
                                disabled={!spot.live || spot.host?.id == 0}
                                onClick={() => handleBook(RESERVATION_TYPE.CALENDAR)}
                            >
                                Find an available time
                            </SniffButton>
                            <hr />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RenderHeadMobile;
